import { inject, Injectable, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { map } from "rxjs/operators";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SnackbarService } from "../snackbar.service";
import { Observable, of, throwError } from "rxjs";
import {
  DataUserResponse,
  LoginResponse,
} from "@core/interfaces/auth.interface";
import { UserStateService } from "./user-state.service";
import { RutaDinamicaService } from "../ruta-dinamica.service";

@Injectable({ providedIn: "root" })
export class AuthenticationService {
  private rutaDinamicaService = inject(RutaDinamicaService);
  private http = inject(HttpClient);
  public snackBar = inject(MatSnackBar);
  public router = inject(Router);
  private snackbar = inject(SnackbarService);
  private userStateService = inject(UserStateService);

  private get authBaseUrl(): string {
    return `${environment.authBaseUrl.replace(/\/+$/, "")}/`;
  }

  public login(form: {
    username: string;
    password: string;
    organization: string;
  }): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(
        `${this.authBaseUrl}usuarios/org/${form.organization}/auth/validate`,
        {
          username: form.username,
          password: form.password,
        },
      )
      .pipe(
        map((response) => {
          // Guardar el token automáticamente si viene en la respuesta
          if (response?.token) {
            sessionStorage.setItem(
              "currentUser",
              JSON.stringify({ token: response.token }),
            );
          }
          return response;
        }),
      );
  }

  public getDataUser(form: {
    username: string;
    password: string;
    organization: string;
  }): Observable<DataUserResponse> {
    if (form.organization) {
      return this.http
        .post<DataUserResponse>(
          `${this.authBaseUrl}usuarios/org/${form.organization}/datosusuario`,
          {
            username: form.username,
          },
        )
        .pipe(
          map((user) => {
            if (user) {
              this.setSessionStorage(user, form.organization);
            }
            return user;
          }),
        );
    } else {
      this.snackbar.warning("Organización incorrecta", 4000);
      return throwError(() => new Error("Organización incorrecta"));
    }
  }

  private setSessionStorage(user: DataUserResponse, organization: string) {
    if (user.token) {
      const currentUser = JSON.stringify({ token: user.token });
      sessionStorage.setItem("currentUser", currentUser);
    }

    // Guardar todos los datos en sessionStorage
    sessionStorage.setItem("usuario", user.usuario.toString());
    sessionStorage.setItem("nivelportal", user.nivelportal.toString());
    sessionStorage.setItem("descnivelportal", user.descnivelportal);
    sessionStorage.setItem("dusuario", user.dusuario);
    if (user.idimagenemp) {
      sessionStorage.setItem("idimagenemp", user.idimagenemp);
    }
    sessionStorage.setItem("idioma", user.idioma);

    // Actualizar el estado del usuario
    this.userStateService.updateUserInfo({
      usuario: user.usuario.toString(),
      rol: user.descnivelportal,
      dusuario: user.dusuario,
      entorno: user.entorno,
      organization: organization,
    });
  }

  public logout() {
    this.rutaDinamicaService.clearMenuCache();
    sessionStorage.removeItem("currentUser");
    sessionStorage.removeItem("nivelportal");
    sessionStorage.removeItem("idioma");
    sessionStorage.removeItem("usuario");
    sessionStorage.removeItem("descnivelportal");
    sessionStorage.removeItem("dusuario");
    sessionStorage.removeItem("idimagenemp");
    sessionStorage.removeItem("organization");
    this.userStateService.clearUserState();
    this.router.navigate(["/login"]);
  }

  public isAuthenticated(): boolean {
    return this.userStateService.isAuthenticated();
  }

  public validate(body: any, organization: string): Observable<any> {
    return this.http.post<any>(
      `${this.authBaseUrl}usuarios/org/${organization}/auth/cambiopass`,
      body,
    );
  }

  public forgotPassword(
    organization: string,
    email: string,
    username: string,
  ): Observable<{ mensaje: string; errortxt: number }> {
    return this.http.post<{ mensaje: string; errortxt: number }>(
      `${this.authBaseUrl}usuarios/org/${organization}/auth/olvido`,
      {
        email,
        username,
      },
    );
  }

  public changePassword(
    iduserpcdr: number,
    newpass: string,
    codigo: string,
  ): Observable<any> {
    return this.http.put<void>(
      `${this.authBaseUrl}usuarios/login/resetpass/${iduserpcdr}`,
      {
        newpass,
        codigo,
      },
    );
  }
}
